<template>
  <vSelect
    v-on="listeners"
    v-model="internalValue"
    v-bind="$attrs"
    :label="label"
    :multiple="multiple"
    :class="{ 'select--error': error, 'select--multiple': multiple }"
    class="select"
  >
    <template #option="option">
      <div class="select__item d-center">
        <slot name="custom-option" v-bind="option">{{ displayField ? displayField(option) : option[label] }}</slot>
      </div>
    </template>
    <template #selected-option="option">
      <div class="selected d-center">
        <slot name="custom-option" v-bind="option">{{ displayField ? displayField(option) : option[label] }}</slot>
      </div>
    </template>
    <template #open-indicator>
      <svg xmlns="http://www.w3.org/2000/svg" width="8" height="5" viewBox="0 0 8 5" fill="none">
        <path d="M7.872.418 7.61.138a.423.423 0 0 0-.619 0l-2.99 3.164L1.01.135a.422.422 0 0 0-.619 0L.128.413a.483.483 0 0 0 0 .655L3.691 4.85a.437.437 0 0 0 .31.149h.002a.437.437 0 0 0 .31-.149l3.56-3.773A.482.482 0 0 0 8 .747a.477.477 0 0 0-.128-.33Z" fill="#606060"/>
      </svg>
    </template>
    <template #no-options="{ search, searching }">
      <div v-if="searching" class="select__noty">No results for your search <em>{{ search }}</em>. Please try again.</div>
      <div v-else class="select__noty">Start typing to search</div>
    </template>
  </vSelect>
</template>

<script>
import vSelect from 'vue-select'

export default {
  name: 'AppSelect',
  components: {vSelect},
  inheritAttrs: false,
  props: {
    value: null,
    error: {
      type: Boolean,
      default: false
    },
    displayField: {
      type: Function,
    },
    label: {
      type: String,
      default: 'name'
    },
    multiple: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      internalValue: this.value
    }
  },
  computed: {
    listeners() {
      const { input, ...listeners } = this.$listeners
      return listeners
    }
  },
  watch: {
    value(newValue) {
      if (newValue !== this.internalValue) this.internalValue = newValue
    },
    internalValue(newValue) {
      if (newValue !== this.value) this.$emit('input', newValue)
    }
  },
}
</script>

<style lang="scss">
@import "vue-select/src/scss/vue-select.scss";

.select {
  .vs__search {
    font-family: $font-family-base;
    font-size: 13px;
    font-weight: 400;
    color: $color-text-base;
    line-height: 1.5;
  }

  .vs__search::placeholder {
    font-family: $font-family-base;
    font-size: 13px;
    font-weight: 400;
    color: $color-text-second;
  }

  .vs__dropdown-toggle {
    position: relative;
    height: 38px;
    padding: 0 0 0 5px;
    border-color: $color-border;
    border-radius: 6px;
    background-color: #fff;
    overflow: hidden;
  }

  img {
    height: auto;
    max-width: 2.5rem;
    margin-right: 4px;
  }

  .d-center {
    display: flex;
    align-items: center;
  }

  .selected {
    white-space: nowrap;
    overflow: hidden;
  }

  .selected .special {
    margin-left: 5px;
  }

  .select__item {
    padding: 3px 0;
    color: $color-text-base;
    font-size: 13px;
    font-weight: 400;
    text-decoration: none;
  }

  .select__noty {
    padding: 3px 15px 4px;
    font-size: 13px;

    span, em {
      font-weight: 600;
    }
  }

  .vs__dropdown-option {
    padding: 2px 12px;

    .select__item {
      color: $color-text-second;
    }

    &.vs__dropdown-option--disabled:not(.vs__dropdown-option--selected) {
      .select__item {
        opacity: 0.5;
      }
    }

    &.vs__dropdown-option--disabled {
      cursor: not-allowed;
    }
  }

  .vs__dropdown-option.vs__dropdown-option--highlight {
    background-color: transparent;

    .select__item {
      color: $color-text-base;
    }
  }

  .vs__dropdown-option.vs__dropdown-option--selected:not(.vs__dropdown-option--highlight) {
    background-color: transparent;

    .select__item {
      color: $color-text-base;
    }
  }

  .vs__selected {
    position: absolute;
    top: 8px;
    margin-top: 0;
    font-family: $font-family-base;
    font-size: 13px;
    color: $color-text-base;
    overflow: hidden;
  }

  .vs__selected-options {
    flex-wrap: nowrap;
    overflow: hidden;
    margin-right: 20px;
  }

  .vs__selected .select__img {
    width: auto;
    max-height: 20px;
    margin-right: 4px;
  }

  .v-select .dropdown li {
    border-bottom: 1px solid rgba(112, 128, 144, 0.1);
  }

  .v-select .dropdown li:last-child {
    border-bottom: none;
  }

  .v-select .dropdown li a {
    padding: 10px 20px;
    width: 100%;
    font-size: 13px;
    color: #3c3c3c;
  }

  .vs__clear {
    margin-right: 0;
    line-height: 0;
  }

  .v-select .dropdown-menu .active > a {
    color: #fff;
  }

  .vs__search, .vs__search:focus {
    margin: 0;
  }

  &.vs--open .vs__actions > svg {
    transform: rotate(180deg);
  }

  &.vs--open.vs--single .vs__selected {
    top: 8px;
  }

  &.vs--open .vs__dropdown-toggle {
    border-bottom-color: $color-border;
    border-radius: 7px 7px 0 0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &.vs--open .select__open-indicator path {
    stroke: $color-theme
  }

  &.vs--loading .open-indicator {
    opacity: 0;
  }

  &.vs--loading img, &.vs--loading svg {
    display: none;
  }

  &[readonly] {
    pointer-events: none;

    .vs__dropdown-toggle {
      border-color: #ff9416;
      padding: 0;
    }

    .vs__actions {
      display: none;
    }

    .vs__deselect {
      display: none;
    }

    .vs__selected {
      padding: 1px 10px;
    }
  }

  .open-indicator {
    transition: transform 0.3s;
  }

  .vs__spinner {
    position: absolute;
    left: 6px;
    top: 6px;
    border-color: rgba($color-theme, 0.2);
    border-left-color: rgba($color-theme, 0.8);
  }

  .vs__dropdown-menu {
    top: 100%;
    max-height: 174px;
    padding-top: 0;
    padding-bottom: 0;
    background-color: #fff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border-radius: 0 0 7px 7px;
    border-width: 0 1px 1px 1px;
    border-style: solid;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-color: $color-theme transparent;
    scrollbar-width: thin;

    &::after {
      content: "";
      margin-bottom: 5px;
    }

    li {
      white-space: normal!important;

      &.vs__no-options {
        padding: 10px;
        font-size: 13px;
      }
    }
  }

  .vs__dropdown-menu::-webkit-scrollbar {
    width: 5px;
  }

  .vs__dropdown-menu::-webkit-scrollbar-track {
    border-radius: 5px;
    background-color: transparent;
  }

  .vs__dropdown-menu::-webkit-scrollbar-thumb {
    border-radius: 5px;
    background-color: $color-theme;
  }

  .vs__dropdown-menu li {
    overflow-wrap: anywhere;
    white-space: break-spaces;
  }

  .vs__actions {
    flex-shrink: 0;
    width: 38px;
    position: relative;
    padding: 0 14px 0 15px;
    background-color: #fafafa;
    border-left: 1px solid $color-border;
  }

  .vs__clear svg {
    display: none;
  }

  .vs__clear::after {
    content: '';
    display: block;
    position: absolute;
    right: 48px;
    top: 50%;
    width: 8px;
    height: 8px;
    background-image: url("~@/assets/img/cancel-icon.svg");
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
    transform: translateY(-50%);
  }

  .select-item {
    padding: 10px 0;
    font-size: 13px;
    font-weight: 600;
    color: $color-text-base;

    &:hover {
      background-color: $color-border;
    }
  }

  &.drop-up {
    &.vs--open {
      .vs__dropdown-toggle {
        border-radius: 0 0 6px 6px;
        //border-bottom-color: $color-theme;
        border-top-color: #c7cfdd;
      }
    }
  }

  &.select--small {
    min-width: 100px;

    .vs__dropdown-menu {
      min-width: auto;
    }
  }
}

.select--notminw {
  min-width: 154px;
}

.select--200 {
  min-width: 200px;
}
.select--180 {
  min-width: 180px;
}

.select--160 {
  min-width: 160px;
}

.select--100 {
  min-width: 100px;
}

.select--max-200 {
  max-width: 200px;
  margin: 0 auto;
}

.select--max-250 {
  max-width: 250px;
  margin: 0 auto;
}

.select--error {
  .vs__dropdown-toggle {
    border-color: $color-error;
  }

  .open-indicator circle {
    fill: $color-error;
  }
}

.select--multiple {
  .vs__dropdown-toggle {
    height: auto;
    min-height: 38px;
  }

  .vs__selected-options {
    flex-wrap: wrap;
    align-items: center;
    padding: 3px 2px;
    overflow: visible;
  }

  .vs__selected {
    position: static;
    margin: 2px;
    padding: 3px 6px 3px 10px;
    border-radius: 20px;
    border: 1px solid $color-border;
    background-color: transparent;
    color: $color-text-base;
    font-size: 13px;
    font-weight: 400;
  }

  .vs__deselect {
    margin-left: 4px;
    padding: 4px;
    border-radius: 50%;

    svg {
      transform: scale(0.8);

      path {
        fill: #8a8a8a;
      }
    }
  }

  .selected {
    white-space: break-spaces;
  }
}

.select--maxw {
  width: 300px;
  margin: 0 auto;

  @media(max-width: 550px) {
    min-width: 100%;
    width: auto;
  }
}

.select--250 {
  width: 250px;
}

.select--300 {
  width: 300px;

  @media(max-width: 550px) {
    min-width: 100%;
    width: auto;
  }
}

.select--minw {
  min-width: 450px;

  @media(max-width: 550px) {
    min-width: 100%;
  }
}

.vs__dropdown-menu::-webkit-scrollbar {
  width: 2px;
}

.vs__dropdown-menu::-webkit-scrollbar-track {
  border-radius: 5px;
  background-color: transparent;
}

.vs__dropdown-menu::-webkit-scrollbar-thumb {
  border-radius: 5px;
  background-color: $color-theme;
}

.vs__dropdown-menu[data-popper-placement="bottom"] {
  top: 100%;
  max-height: 174px;
  padding-top: 0;
  padding-bottom: 0;
  background-color: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-radius: 0 0 7px 7px;
  border-width: 0 1px 1px 1px;
  border-style: solid;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-color: $color-theme transparent;
  scrollbar-width: thin;

  &::after {
    content: "";
    margin-bottom: 5px;
  }

  li {
    white-space: normal!important;

    &.vs__no-options {
      padding: 10px;
      font-size: 13px;
    }
  }

  .vs__dropdown-option {
    padding: 4px 12px;

    .select__item {
      color: $color-text-second;
      font-size: 13px;
    }
  }

  .vs__dropdown-option.vs__dropdown-option--highlight {
    background-color: transparent;

    .select__item {
      color: $color-text-base;
    }
  }

  .vs__dropdown-option.vs__dropdown-option--selected:not(.vs__dropdown-option--highlight) {
    background-color: transparent;

    .select__item {
      color: $color-text-base;
    }
  }
}

.vs__dropdown-menu[data-popper-placement="top"] {
  top: 100%;
  max-height: 174px;
  padding-top: 0;
  padding-bottom: 0;
  background-color: #fff;
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.1);
  border-radius: 7px 7px 0 0;
  border-width: 1px 1px 0 1px;
  border-style: solid;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-color: $color-theme transparent;
  scrollbar-width: thin;

  &::after {
    content: "";
    margin-bottom: 5px;
  }

  li {
    white-space: normal!important;

    &.vs__no-options {
      padding: 10px;
      font-size: 13px;
    }
  }

  .vs__dropdown-option {
    padding: 4px 12px;

    .select__item {
      color: $color-text-second;
      font-size: 13px;
    }
  }

  .vs__dropdown-option.vs__dropdown-option--highlight {
    background-color: transparent;

    .select__item {
      color: $color-text-base;
    }
  }

  .vs__dropdown-option.vs__dropdown-option--selected:not(.vs__dropdown-option--highlight) {
    background-color: transparent;

    .select__item {
      color: $color-text-base;
    }
  }
}

.select.vs--disabled .vs__search {
  background-color: #fff;
}
</style>
